import { useState, useCallback, useEffect } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Divider, Loader } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'

import { navigate, routes } from '@redwoodjs/router'
import { Metadata, useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import QuestionRow from 'src/components/Inputs/QuestionRow/QuestionRow'
import { PDFPreviewModal } from 'src/components/Overlays/PDFPreviewModal/PDFPreviewModal'
import SwitchCompanyModal from 'src/components/Overlays/SwitchCompanyModal/SwitchCompanyModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_DRIVER_CONSENT_URL_TO_DOWNLOAD } from 'src/graphql/consent.graphql'
import { VoEStatus } from 'src/graphql/types/verificationOfEmployments'
import {
  GET_VOE_BY_ID,
  GET_VOE_FILE_DOWNLOAD_URL,
  MARK_VOE_AS_COMPLETED,
} from 'src/graphql/voe.graphql'
import { useVoECompanySwitch } from 'src/hooks/useVoECompanySwitch'
import IconAlertCircle from 'src/icons/IconAlertCircle'
import { canViewVoEResponse } from 'src/lib/voeResponseAccess'
import { voeResponseFormSchema } from 'src/lib/voeResponseFormSchema'
import { downloadFile } from 'src/utils/downloadFile'

import AccidentsCard from '../ApplicantDetailsPage/tabs/ApplicationTab/components/AccidentsCard'
import AuditTrailCard from '../VoERequestDetailsPage/components/AuditTrailCard'
import CompanyInfoCard from '../VoERequestDetailsPage/components/CompanyInfoCard'
import DriverInfoCard from '../VoERequestDetailsPage/components/DriverInfoCard'
import RequestContentColumn from '../VoERequestDetailsPage/components/RequestContentColumn'
import RequestInfoCard from '../VoERequestDetailsPage/components/RequestInfoCard'
import ResponseContentColumn from '../VoERequestDetailsPage/components/ResponseContentColumn'
import VoEConsentCard from '../VoERequestDetailsPage/components/VoEConsentCard'
import VoEDetailsHeader from '../VoERequestDetailsPage/components/VoEDetailsHeader'

const VoEResponseDetailPage = ({ voeId }) => {
  const [openConsent, setOpenConsent] = useState(false)
  const [previewDoc, setPreviewDoc] = useState(null)
  const [driverConsentFileUrl, setDriverConsentFileUrl] = useState()

  const form = useForm({
    validate: zodResolver(voeResponseFormSchema),
  })

  const [submitVoEResponse, { loading: loadingSubmit }] = useMutation(
    MARK_VOE_AS_COMPLETED,
    {
      refetchQueries: [
        {
          query: GET_VOE_BY_ID,
          variables: {
            id: voeId,
          },
        },
      ],
      onCompleted: () => {
        toast('VoE marked as completed', 'success')
      },
      onError: () => {
        toast('Failed to mark VoE as completed', 'error')
      },
    }
  )

  const {
    handleVoELoadError,
    switchTarget,
    isSwitching,
    confirmSwitch,
    cancelSwitch,
  } = useVoECompanySwitch(voeId)

  const { data, loading } = useQuery(GET_VOE_BY_ID, {
    variables: {
      id: voeId,
    },
    onCompleted: () => {
      handleInitialValues()
    },
    onError: (error) => {
      handleVoELoadError(error)
    },
  })

  const voe = data?.verificationOfEmployment

  const hasResponse = canViewVoEResponse(voe)
  const unansweredApplicantId =
    voe && !hasResponse ? voe.applicant?.applicantId : undefined

  useEffect(() => {
    if (!unansweredApplicantId) return
    navigate(
      routes.voeResponseReview({
        id: unansweredApplicantId,
        tab: 'voe',
        voeId,
      }),
      { replace: true }
    )
  }, [unansweredApplicantId, voeId])

  const [fetchDownloadUrl] = useLazyQuery(GET_VOE_FILE_DOWNLOAD_URL, {
    fetchPolicy: 'network-only',
  })

  const [getDriverFileUrl, { loading: loadingPreviewConsent }] = useLazyQuery(
    GET_DRIVER_CONSENT_URL_TO_DOWNLOAD,
    {
      variables: {
        applicationId: voe?.applicant?.application?.applicationId,
      },
      onCompleted: ({ getDriverConsentFileDownloadUrlByApplication: url }) => {
        setDriverConsentFileUrl(url)
        setOpenConsent(true)
      },
      onError: () => {
        toast('Unable to get driver consent file, please try again.', 'error')
      },
    }
  )

  const handleSubmit = () => {
    submitVoEResponse({
      variables: {
        id: voeId,
        input: {
          completionMethod: 'DOUBLE_NICKEL_FORM',
        },
      },
    })
  }

  const handleInitialValues = () => {
    if (!voe) return

    form.setValues(
      voe.status === VoEStatus.PENDING_REVIEW ||
        voe.status === VoEStatus.COMPLETED
        ? voe.responseContent
          ? {
              positionHeld: voe.responseContent.positionHeld,
              startDate: new Date(voe.responseContent.startDate),
              endDate: voe.responseContent.endDate
                ? new Date(voe.responseContent.endDate)
                : null,
              reasonForLeaving: voe.responseContent.reasonForLeaving,
              trailerTypes: voe.responseContent.trailerTypes,
              eligibleForRehire: voe.responseContent.eligibleForRehire || false,
              isDriverTerminated: voe.responseContent.isDriverTerminated,
              isDriverSubjectToFMCSRs:
                voe.responseContent.isDriverSubjectToFMCSRs,
              isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
                voe.responseContent
                  .isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
              accidents: voe.accidents,
            }
          : {}
        : {
            accidents: voe.accidents,
            positionHeld: voe.requestContent.positionHeld,
            startDate: new Date(voe.requestContent.startDate),
            endDate: voe.requestContent.endDate
              ? new Date(voe.requestContent.endDate)
              : null,
            reasonForLeaving: voe.requestContent.reasonForLeaving,
            trailerTypes: voe.requestContent.trailerTypes,
            eligibleForRehire: voe.requestContent.eligibleForRehire || false,
            isDriverTerminated: voe.requestContent.isDriverTerminated,

            isDriverSubjectToFMCSRs: voe.requestContent.isDriverSubjectToFMCSRs,
            isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
              voe.requestContent
                .isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
          }
    )
  }

  const isCompleted = voe?.status === VoEStatus.COMPLETED

  const handleAccidentsUpdate = async ({ accidents }) => {
    form.setFieldValue(`accidents`, accidents)
    return { errors: null }
  }

  const onPreviewDriverConsent = useCallback(() => {
    if (!driverConsentFileUrl) {
      getDriverFileUrl()
    } else {
      setOpenConsent(true)
    }
  }, [driverConsentFileUrl, getDriverFileUrl])

  const onConsentDownload = useCallback(() => {
    downloadFile({
      url: driverConsentFileUrl,
      fileName: 'consent.pdf',
    })
  }, [driverConsentFileUrl])

  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Loader />
      </div>
    )
  }

  const notFound = (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="text-lg text-doubleNickel-gray-600">
        VoE data not found
      </div>
    </div>
  )

  if (!voe) {
    return (
      <>
        <SwitchCompanyModal
          opened={!!switchTarget}
          applicantName={switchTarget?.driverName ?? ''}
          companyName={switchTarget?.companyName ?? ''}
          isSwitching={isSwitching}
          onCancel={cancelSwitch}
          onConfirm={confirmSwitch}
        />
        {notFound}
      </>
    )
  }

  if (!hasResponse) {
    if (unansweredApplicantId) {
      // Redirecting to the applicant's VoE tab
      return (
        <div className="flex h-full flex-col items-center justify-center">
          <Loader />
        </div>
      )
    }
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <div className="text-lg text-doubleNickel-gray-600">
          This verification has no employer response yet
        </div>
        <div className="text-sm text-doubleNickel-gray-500">
          Outreach attempts and next steps are tracked on the VoE itself.
        </div>
      </div>
    )
  }

  return (
    <form
      className="flex h-full flex-col gap-6  overflow-auto px-6 pb-10"
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
    >
      <Metadata title="VoE Response" description="Voe Response page" />
      <VoEDetailsHeader
        showSubmitButton={!isCompleted}
        submitButtonText="Mark as completed"
        loadingSubmit={loadingSubmit}
        voe={voe}
        title="VoE Response"
        showNameMismatchCheck={false}
      />
      <RequestInfoCard voe={voe} />
      <CompanyInfoCard voe={voe} />
      <DriverInfoCard
        voe={voe}
        noApplicantLink={false}
        showNameMismatchCheck={false}
      />

      {/* REQUEST CONTENT */}
      <div className="flex flex-col gap-6 rounded-lg border border-doubleNickel-gray-200">
        <div className="flex flex-col gap-6 p-6">
          <div className="text-md font-semibold text-doubleNickel-gray-900">
            Request Content
          </div>
          <div className="flex flex-row gap-8 ">
            <RequestContentColumn form={form} request={voe?.requestContent} />
            <Divider orientation="vertical" />
            <ResponseContentColumn
              editing={false}
              form={form}
              request={voe?.requestContent}
            />
          </div>
          <div className="flex flex-row items-center gap-4 rounded-lg bg-doubleNickel-brand-25 px-4 py-3">
            <IconAlertCircle className="fill-none stroke-doubleNickel-brand-500" />
            <div className="flex-1">
              <QuestionRow
                required
                text="Is this applicant eligible for rehire?"
                value={voe?.responseContent?.eligibleForRehire || null}
                handleChange={(value) =>
                  form.setFieldValue(`eligibleForRehire`, value === 'true')
                }
                editing={false}
              />
            </div>
          </div>
        </div>
      </div>
      {form.values.accidents && (
        <AccidentsCard
          values={form.values.accidents}
          handleUpdateApplication={handleAccidentsUpdate}
          readOnly={true}
        />
      )}
      <VoEConsentCard
        onPreview={onPreviewDriverConsent}
        loading={loadingPreviewConsent}
        documents={voe?.documents || []}
        onDocumentClick={async (doc) => {
          if (doc.voeDocumentType === 'CONSENT_PDF') {
            onPreviewDriverConsent()
          } else if (doc.voeDocumentType === 'RESPONSE_PDF') {
            try {
              const { data } = await fetchDownloadUrl({
                variables: { voeId: voe.voeId, fileType: 'response' },
              })
              if (data?.voeFileDownloadUrl) {
                setPreviewDoc({
                  fileName: doc.fileName || 'VoE Response',
                  url: data.voeFileDownloadUrl,
                })
              } else {
                toast('File not available', 'error')
              }
            } catch {
              toast('Error loading document', 'error')
            }
          }
        }}
      />

      {openConsent && (
        <PDFPreviewModal
          fileName="Driver consent"
          url={driverConsentFileUrl}
          isOpen={openConsent}
          onClose={() => setOpenConsent(null)}
          onBack={() => setOpenConsent(null)}
          onDownload={onConsentDownload}
        />
      )}

      {previewDoc && (
        <PDFPreviewModal
          fileName={previewDoc.fileName}
          url={previewDoc.url}
          isOpen={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
          onBack={() => setPreviewDoc(null)}
          onDownload={() =>
            downloadFile({
              url: previewDoc.url,
              fileName: previewDoc.fileName,
            })
          }
        />
      )}

      <AuditTrailCard auditTrailLogs={voe?.auditTrailLogs} />

      {!isCompleted && (
        <div className="flex w-full flex-row justify-end">
          <Button
            text="Mark as completed"
            loading={loadingSubmit}
            type="submit"
          />
        </div>
      )}
    </form>
  )
}

export default VoEResponseDetailPage
